import React, { useEffect, useState, useMemo } from "react";
import { message } from "antd";
import {
  MenuOrder, OrderStatus,
  getMenuOrders, updateMenuOrderStatus,
} from "../../../utils/api/menuOrdersAPI";
import { ClientApartment } from "../../../utils/api/apartmentRentalsAPI";

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  clientId: string;
  apartments: ClientApartment[];
}

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_LABEL: Record<OrderStatus, string> = {
  pending: "Na čekanju",
  confirmed: "Potvrđena",
  delivered: "Isporučena",
  cancelled: "Otkazana",
};

const STATUS_COLOR: Record<OrderStatus, { bg: string; text: string; border: string }> = {
  pending:   { bg: "#fef9c3", text: "#854d0e", border: "#fde047" },
  confirmed: { bg: "#dbeafe", text: "#1e40af", border: "#93c5fd" },
  delivered: { bg: "#dcfce7", text: "#166534", border: "#86efac" },
  cancelled: { bg: "#fee2e2", text: "#991b1b", border: "#fca5a5" },
};

const ALL_STATUSES: OrderStatus[] = ["pending", "confirmed", "delivered", "cancelled"];

const fmtMoney = (n: number, currency: string) => {
  if (currency === "EUR") return `€${n % 1 === 0 ? n : n.toFixed(2)}`;
  if (currency === "BAM") return `${n} KM`;
  return `${n} RSD`;
};

const fmtDate = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleString("bs-BA", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

// ─── Status badge ─────────────────────────────────────────────────────────────

const StatusBadge: React.FC<{ status: OrderStatus }> = ({ status }) => {
  const c = STATUS_COLOR[status];
  return (
    <span style={{ display: "inline-block", padding: "3px 9px", borderRadius: 6, fontSize: 11, fontWeight: 700, background: c.bg, color: c.text, border: `1px solid ${c.border}` }}>
      {STATUS_LABEL[status]}
    </span>
  );
};

// ─── Order detail modal ───────────────────────────────────────────────────────

interface DetailModalProps { order: MenuOrder; onClose: () => void; onStatusChange: (id: string, s: OrderStatus) => Promise<void>; }
const DetailModal: React.FC<DetailModalProps> = ({ order, onClose, onStatusChange }) => {
  const [saving, setSaving] = useState(false);

  const handleStatus = async (s: OrderStatus) => {
    setSaving(true);
    try { await onStatusChange(order._id, s); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1200, padding: 16 }}
      onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 500, maxHeight: "90vh", overflowY: "auto", boxShadow: "0 8px 40px rgba(0,0,0,0.18)" }}
        onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div style={{ padding: "16px 20px", borderBottom: "1px solid #f0edf8", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, background: "#fff", zIndex: 1 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#1a1520" }}>Detalji narudžbe</div>
            <div style={{ fontSize: 11, color: "#9893a8", marginTop: 2 }}>{fmtDate(order.createdAt)}</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <StatusBadge status={order.status} />
            <button type="button" onClick={onClose} style={{ background: "none", border: "none", fontSize: 22, color: "#9893a8", cursor: "pointer" }}>×</button>
          </div>
        </div>

        <div style={{ padding: "16px 20px", display: "flex", flexDirection: "column", gap: 16 }}>

          {/* Info rows */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            {[
              { label: "Apartman", value: order.apartmentName },
              { label: "Kategorija", value: order.categoryName || "—" },
              { label: "Dobavljač", value: order.supplierName || "—" },
              { label: "Email gosta", value: order.guestEmail },
              { label: "Telefon", value: order.guestPhone },
            ].map(({ label, value }) => (
              <div key={label} style={{ background: "#f8f7ff", borderRadius: 8, padding: "9px 12px" }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#9893a8", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 3 }}>{label}</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#1a1520", wordBreak: "break-word" }}>{value}</div>
              </div>
            ))}
          </div>

          {/* Items */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#9893a8", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>Naručene stavke</div>
            <div style={{ border: "1.5px solid #e8e5f0", borderRadius: 10, overflow: "hidden" }}>
              {order.items.map((it, idx) => (
                <div key={idx} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "9px 14px", borderBottom: idx < order.items.length - 1 ? "1px solid #f0edf8" : "none", background: idx % 2 === 0 ? "#fff" : "#fafaf9" }}>
                  <span style={{ fontSize: 13, color: "#374151" }}>{it.itemName}</span>
                  <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                    <span style={{ fontSize: 12, color: "#9893a8" }}>× {it.quantity}</span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: "#1a1520" }}>{fmtMoney(it.price * it.quantity, order.currency)}</span>
                  </div>
                </div>
              ))}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", background: "#1a1520" }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>UKUPNO</span>
                <span style={{ fontSize: 16, fontWeight: 800, color: "#a78bfa" }}>{fmtMoney(order.total, order.currency)}</span>
              </div>
            </div>
          </div>

          {/* Note */}
          {order.note && (
            <div style={{ padding: "10px 14px", background: "#f5f3ff", borderRadius: 8, fontSize: 13, color: "#374151", borderLeft: "3px solid #7c6ef5" }}>
              <strong>Napomena:</strong> {order.note}
            </div>
          )}

          {/* Status change */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#9893a8", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>Promijeni status</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {ALL_STATUSES.map((s) => {
                const c = STATUS_COLOR[s];
                const active = order.status === s;
                return (
                  <button key={s} type="button" disabled={saving || active} onClick={() => handleStatus(s)}
                    style={{ padding: "7px 14px", borderRadius: 8, border: `1.5px solid ${active ? c.border : "#e8e5f0"}`, background: active ? c.bg : "#fff", color: active ? c.text : "#6b7280", fontSize: 12, fontWeight: 600, cursor: active || saving ? "default" : "pointer", opacity: saving ? 0.6 : 1 }}>
                    {STATUS_LABEL[s]}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main ─────────────────────────────────────────────────────────────────────

const MenuOrdersTab: React.FC<Props> = ({ clientId, apartments }) => {
  const [orders, setOrders] = useState<MenuOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState<MenuOrder | null>(null);

  // Filters
  const [filterApt, setFilterApt] = useState("");
  const [filterStatus, setFilterStatus] = useState<OrderStatus | "">("");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");

  const load = () => {
    setLoading(true);
    getMenuOrders(clientId, {
      apartmentId: filterApt || undefined,
      status: (filterStatus || undefined) as OrderStatus | undefined,
      from: filterFrom || undefined,
      to: filterTo || undefined,
    })
      .then((res) => setOrders(res.data))
      .catch(() => message.error("Greška pri učitavanju narudžbi"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [clientId]);

  const handleStatusChange = async (id: string, status: OrderStatus) => {
    const res = await updateMenuOrderStatus(id, status);
    setOrders((prev) => prev.map((o) => o._id === id ? res.data : o));
    if (detail?._id === id) setDetail(res.data);
    message.success("Status ažuriran");
  };

  // Stats
  const stats = useMemo(() => {
    const total = orders.length;
    const pending = orders.filter((o) => o.status === "pending").length;
    const revenue = orders.filter((o) => o.status !== "cancelled").reduce((s, o) => s + o.total, 0);
    return { total, pending, revenue };
  }, [orders]);

  const inputStyle: React.CSSProperties = { padding: "7px 11px", border: "1.5px solid #e8e5f0", borderRadius: 8, fontSize: 13, fontFamily: "inherit", outline: "none", background: "#fff" };

  return (
    <div style={{ padding: "24px 0" }}>

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 24 }}>
        {[
          { label: "Ukupno narudžbi", value: stats.total, color: "#7c6ef5" },
          { label: "Na čekanju", value: stats.pending, color: "#f59e0b" },
          { label: "Prihod (bez otkazanih)", value: `€${stats.revenue.toFixed(0)}`, color: "#16a34a" },
        ].map(({ label, value, color }) => (
          <div key={label} style={{ background: "#fff", border: "1.5px solid #e8e5f0", borderRadius: 12, padding: "14px 16px" }}>
            <div style={{ fontSize: 22, fontWeight: 800, color }}>{value}</div>
            <div style={{ fontSize: 11, color: "#9893a8", marginTop: 3 }}>{label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16, alignItems: "flex-end" }}>
        <select value={filterApt} onChange={(e) => setFilterApt(e.target.value)} style={inputStyle}>
          <option value="">Svi apartmani</option>
          {apartments.map((a) => <option key={a._id} value={a._id}>{a.name}</option>)}
        </select>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value as any)} style={inputStyle}>
          <option value="">Svi statusi</option>
          {ALL_STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
        </select>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <input type="date" value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)} style={inputStyle} />
          <span style={{ color: "#9893a8", fontSize: 12 }}>–</span>
          <input type="date" value={filterTo} onChange={(e) => setFilterTo(e.target.value)} style={inputStyle} />
        </div>
        <button type="button" onClick={load}
          style={{ padding: "7px 18px", border: "none", borderRadius: 8, background: "#7c6ef5", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
          Filtriraj
        </button>
        {(filterApt || filterStatus || filterFrom || filterTo) && (
          <button type="button" onClick={() => { setFilterApt(""); setFilterStatus(""); setFilterFrom(""); setFilterTo(""); }}
            style={{ padding: "7px 14px", border: "1.5px solid #e8e5f0", borderRadius: 8, background: "#fff", color: "#9893a8", fontSize: 13, cursor: "pointer" }}>
            Resetuj
          </button>
        )}
      </div>

      {/* Orders table */}
      {loading ? (
        <div style={{ padding: 40, textAlign: "center", color: "#9893a8" }}>Učitavanje…</div>
      ) : orders.length === 0 ? (
        <div style={{ padding: "48px 32px", textAlign: "center", background: "#f8f7ff", borderRadius: 14, border: "1.5px dashed #c4bfde" }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>🛒</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#1a1520", marginBottom: 6 }}>Nema narudžbi</div>
          <div style={{ fontSize: 12, color: "#9893a8" }}>Narudžbe će se pojaviti ovdje kada gosti naruče putem QR koda</div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {orders.map((order) => (
            <div key={order._id}
              style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", background: "#fff", border: "1.5px solid #e8e5f0", borderRadius: 12, cursor: "pointer", transition: "border-color 0.15s" }}
              onClick={() => setDetail(order)}
              onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#a78bfa")}
              onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#e8e5f0")}
            >
              {/* Status dot */}
              <div style={{ width: 10, height: 10, borderRadius: "50%", background: STATUS_COLOR[order.status].border, flexShrink: 0 }} />

              {/* Main info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#1a1520" }}>{order.apartmentName}</span>
                  {order.categoryName && (
                    <span style={{ fontSize: 11, color: "#7c6ef5", background: "#f5f3ff", padding: "1px 7px", borderRadius: 5 }}>{order.categoryName}</span>
                  )}
                  {order.supplierName && (
                    <span style={{ fontSize: 11, color: "#9893a8" }}>· {order.supplierName}</span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: "#6b7280", marginTop: 3 }}>
                  {order.guestEmail} · {order.guestPhone}
                </div>
                <div style={{ fontSize: 11, color: "#9893a8", marginTop: 2 }}>
                  {order.items.length} stavki · {fmtDate(order.createdAt)}
                </div>
              </div>

              {/* Total + status */}
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6, flexShrink: 0 }}>
                <span style={{ fontSize: 15, fontWeight: 800, color: "#1a1520" }}>{fmtMoney(order.total, order.currency)}</span>
                <StatusBadge status={order.status} />
              </div>
            </div>
          ))}
        </div>
      )}

      {detail && (
        <DetailModal order={detail} onClose={() => setDetail(null)} onStatusChange={handleStatusChange} />
      )}
    </div>
  );
};

export default MenuOrdersTab;
